/**
 * 统一 Completed 卡片模板 - AI Create / Community / GitHub 三处 completed 卡片共用。
 * 模块对外只暴露 buildCompletedCardHtml / completedCardElement / buildAiJobCardHtml / aiJobCardElement 四个函数，调用方不感知内部 HTML 结构。
 */
import { escapeHtml } from "./formatUtils";
import { DEFAULT_COVER_URL } from '../../domain/topicCover';
import { AI_CHAPTER_AUTO_PLAN_LABEL } from '../../domain/aiChapterTargetPolicy';
import {
	buildAiTaskProgressPresentation,
	type ProgressPresentationStage,
} from './aiTaskProgressPresentation';

/** P0-04/G1: 四阶段进度数据 (来自 content_task_progress 表) */
export interface TaskStageProgressData {
	status: string;
	currentStage: string | null;
	totalChapters: number;
	article: string;       // pending / generating / completed / failed
	podcastScript: string; // pending / generating / completed / failed
	audio: { completed: number; total: number; failed: number };
	video: { completed: number; total: number; failed: number };
	failedStage: string | null;
	errorCode: string | null;
}

export interface CompletedCardOptions {
	title: string;
	chapterCount: number;
	/** ISO 字符串 -> "Created 3 days ago" */
	createdAt: string;
	/** 卡片整体点击 -> showTopicDetail(topicSlug) */
	topicSlug: string;
	/** 状态 pill 文案，如 "Completed" / "Course Ready" */
	statusLabel: string;
	/** pill CSS class，如 "pill-pending" / "pill-completed" */
	statusCls: string;
	/** emoji 缩略图；空/null/🧠 -> 默认封面图 */
	coverIcon?: string;
	/** 缩略图背景色（CSS var 或颜色值） */
	coverColor?: string;
	/** 注入 foot 行的额外 HTML（如 Community 的 View Post 按钮） */
	extraFootHtml?: string;
}

export interface AiJobCardOptions {
	/** AI Create job UUID，用于 showAiJobDetail */
	jobId: string;
	title: string;
	/** 副标题；为空时用 description 截断 */
	subtitle: string;
	/** 章节估算展示文案（如 "9-12" / "15" / "~8"），直接拼为 "X chapters" */
	chapterEstimate: string;
	/** 参考链接数 */
	linksCount: number;
	/** 附件数 */
	attachmentsCount: number;
	/** ISO 字符串 -> "Today" / "3d ago" */
	createdAt: string;
	/** P0-04/G1: 四阶段进度数据（null = 无进度数据） */
	progress?: TaskStageProgressData | null;
	/** Optional top-level job status; terminal values override stale progress status. */
	status?: string;
	/** Whether the generated topic still resolves to a user-visible topic. */
	topicAvailable?: boolean;
}

/**
 * 将 ISO 日期转为相对时间文案："Today" / "Yesterday" / "3 days ago" / "2 weeks ago" / "1 months ago"
 */
function relativeDays(iso: string): string {
	const created = new Date(iso).getTime();
	if (Number.isNaN(created)) return "Recently";
	const days = Math.max(0, Math.floor((Date.now() - created) / (24 * 60 * 60 * 1000)));
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days} days ago`;
	if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
	return `${Math.floor(days / 30)} months ago`;
}

export function buildCompletedCardHtml(opts: CompletedCardOptions): string {
	const chapterLabel = `${opts.chapterCount} ${opts.chapterCount === 1 ? "chapter" : "chapters"}`;
	const hasIcon = !!(opts.coverIcon && opts.coverIcon.trim() && opts.coverIcon !== "🧠");
	const thumbStyle = opts.coverColor ? ` style="background:${escapeHtml(opts.coverColor)};color:#fff;"` : "";
	const thumbHtml = hasIcon
		? `<div class="ai-created-thumb"${thumbStyle}>${escapeHtml(opts.coverIcon!)}</div>`
		: `<div class="ai-created-thumb ai-created-thumb--default" style="background-image:url('${DEFAULT_COVER_URL}')"></div>`;
	return `
		<div class="ai-created-item" data-proto-click="showTopicDetail('${escapeHtml(opts.topicSlug)}')">
			${thumbHtml}
			<div class="ai-created-info">
				<h4>${escapeHtml(opts.title)}</h4>
				<p>${escapeHtml(chapterLabel)}</p>
				<p class="ai-created-foot">Created ${escapeHtml(relativeDays(opts.createdAt))}${opts.extraFootHtml ?? ""}</p>
			</div>
			<div class="ai-created-status"><span class="pill ${escapeHtml(opts.statusCls)}">${escapeHtml(opts.statusLabel)}</span></div>
		</div>
	`;
}

export function completedCardElement(opts: CompletedCardOptions): HTMLElement {
	const template = document.createElement("template");
	template.innerHTML = buildCompletedCardHtml(opts);
	return template.content.firstElementChild as HTMLElement;
}

/** SVG icons for the job foot row (match Topic card's chapter icon). */
const CHAPTER_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>';
const LINK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
const ATTACH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>';

/** 将章节估算 + 链接/附件数 + 相对时间组装为 foot 行文案（含 SVG 图标） */
function buildJobFoot(opts: AiJobCardOptions): string {
	const linkLabel = `${opts.linksCount} link${opts.linksCount === 1 ? "" : "s"}`;
	const attachLabel = `${opts.attachmentsCount} Attachment${opts.attachmentsCount === 1 ? "" : "s"}`;
	const chapterLabel = opts.chapterEstimate === AI_CHAPTER_AUTO_PLAN_LABEL
		? AI_CHAPTER_AUTO_PLAN_LABEL
		: `${opts.chapterEstimate} chapters`;
	const parts: string[] = [
		`<span class="job-foot-item">${CHAPTER_SVG} ${escapeHtml(chapterLabel)}</span>`,
		`<span class="job-foot-item">${LINK_SVG} ${linkLabel}</span>`,
		`<span class="job-foot-item">${ATTACH_SVG} ${attachLabel}</span>`,
		`<span class="job-foot-time">${escapeHtml(relativeDays(opts.createdAt))}</span>`,
	];
	return parts.join(' · ');
}


/**
 * Compact, safe four-stage AI Create progress rail.
 */
function progressIssueId(value: string): string {
	const normalized = value.replace(/[^a-zA-Z0-9_-]/g, '-').slice(0, 80);
	return `ai-progress-issue-${normalized || 'job'}`;
}

const ISSUE_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="6"/><path d="M8 4.5v4"/><path d="M8 11.8h.01"/></svg>';

export function buildTaskStageProgressHtml(p: TaskStageProgressData, jobStatus?: string, issueId = 'ai-progress-issue', topicAvailable = true): string {
	const presentation = buildAiTaskProgressPresentation(p, jobStatus, topicAvailable);
	const stageIcon = (stage: ProgressPresentationStage): string => {
		const status = stage.state === 'completed' ? 'Completed' : stage.state === 'active' ? 'In progress' : 'Pending';
		const visual = stage.state === 'active'
			? '<span class="tsp-spinner" aria-hidden="true"></span>'
			: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${stage.state === 'completed'
				? '<path d="m3.5 8.2 2.7 2.7 6.3-6.3"/>'
				: '<circle cx="8" cy="8" r="5.5"/>'}</svg>`;
		return `<span class="tsp-node tsp-${stage.state}" role="img" aria-label="${status}">${visual}</span>`;
	};
	const items = presentation.stages.map((stage) => {
		const countHtml = stage.countLabel ? `<span class="tsp-count">${escapeHtml(stage.countLabel)}</span>` : '';
		return `<span class="tsp-stage">${stageIcon(stage)}<span class="tsp-label">${escapeHtml(stage.label)}</span>${countHtml}</span>`;
	}).join('<span class="tsp-divider" aria-hidden="true"></span>');
	const safeIssueId = escapeHtml(issueId);
	const issueHtml = presentation.issue
		? `<button type="button" class="ai-job-progress-issue-trigger" aria-label="View generation issue" aria-describedby="${safeIssueId}" aria-controls="${safeIssueId}" aria-expanded="false">${ISSUE_SVG}</button>`
		: '';
	const summaryIcon = presentation.summaryRunning ? '<span class="tsp-spinner" aria-hidden="true"></span>' : '';
	const tooltipHtml = presentation.issue
		? `<div id="${safeIssueId}" class="ai-job-progress-tooltip" role="tooltip" hidden>${escapeHtml(presentation.issue.text)}</div>`
		: '';

	const summaryStateClass = presentation.summaryRunning
		? ' is-running'
		: presentation.issue?.kind === 'not_completed'
			? ' is-failed'
			: presentation.issue?.kind === 'completed_with_issue' ? ' is-warning' : '';
	return `<div class="ai-job-progress-shell"><div class="task-stage-progress" role="group" aria-label="Generation progress"><span class="ai-job-progress-summary${summaryStateClass}">${summaryIcon}<span>${escapeHtml(presentation.summaryLabel)}</span>${issueHtml}</span><span class="ai-job-progress-separator" aria-hidden="true"></span>${items}</div>${tooltipHtml}</div>`;
}

/**
 * In-Progress / failed AI Create 卡片 - 横行布局。
 * 复用 .ai-created-item CSS 基础（与 Completed 横行卡片同一设计语言），
 * 点击整行进入 showAiJobDetail(jobId)。
 * 以后 GitHub / Community 的 in-progress 卡片可复用此模板。
 */
export function buildAiJobCardHtml(opts: AiJobCardOptions): string {
	const progressHtml = opts.progress
		? buildTaskStageProgressHtml(opts.progress, opts.status, progressIssueId(opts.jobId), opts.topicAvailable)
		: "";
	return `
		<div class="ai-created-item ai-job-row" data-proto-click="showAiJobDetail('${escapeHtml(opts.jobId)}')">
			<div class="ai-created-thumb ai-created-thumb--default" style="background-image:url('${DEFAULT_COVER_URL}')"></div>
			<div class="ai-created-info">
				<h4><button type="button" class="ai-job-title-action" aria-label="View AI Create job for ${escapeHtml(opts.title)}" data-proto-click="showAiJobDetail('${escapeHtml(opts.jobId)}')">${escapeHtml(opts.title)}</button></h4>
				<p>${escapeHtml(opts.subtitle)}</p>
				<p class="ai-created-foot">${buildJobFoot(opts)}</p>
				${progressHtml}
			</div>
		</div>
	`;
}

export function aiJobCardElement(opts: AiJobCardOptions): HTMLElement {
	const template = document.createElement("template");
	template.innerHTML = buildAiJobCardHtml(opts);
	return template.content.firstElementChild as HTMLElement;
}
